import { useState, useMemo } from 'react';
import { Search, Star, History, ChevronLeft, ChevronRight, Sparkles, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { usePlaygroundStore } from '@/store/playground';
import { useModels, useTasks } from '@/hooks/useModels';
import type { ModelInfo, ModelTaskType } from '@shared/types';

// Category shortcuts shown as tabs (not every task, just key groups)
const CATEGORY_TABS = [
  { id: null,                       label: 'All',     icon: '\u2726' },
  { id: 'text-generation',          label: 'Text',    icon: '\uD83D\uDCAC' },
  { id: 'text-to-image',            label: 'Image',   icon: '\uD83C\uDFA8' },
  { id: 'automatic-speech-recognition', label: 'Speech', icon: '\uD83C\uDF99' },
  { id: 'image-to-text',            label: 'Vision',  icon: '\uD83D\uDC41' },
] as const;

const TASK_ICONS: Record<string, string> = {
  'text-generation': '\uD83D\uDCAC',
  'text-to-image': '\uD83C\uDFA8',
  'image-to-text': '\uD83D\uDC41',
  'automatic-speech-recognition': '\uD83C\uDF99',
  translation: '\uD83C\uDF10',
  'text-embeddings': '\uD83D\uDD22',
  'text-classification': '\uD83C\uDFF7',
  'image-classification': '\uD83D\uDCF8',
  'object-detection': '\uD83D\uDD0D',
  summarization: '\uD83D\uDCDD',
  unknown: '\u2753',
};

export function Sidebar() {
  const {
    sidebarOpen, setSidebarOpen,
    selectedTask, setSelectedTask,
    searchQuery, setSearchQuery,
    favorites, openTab, tabs, activeTabId,
  } = usePlaygroundStore();

  const [activeView, setActiveView] = useState<'models' | 'favorites' | 'history'>('models');

  const { data: modelsData, isLoading: modelsLoading } = useModels();
  const { data: tasksData } = useTasks();

  // The model ID of the currently active tab
  const activeModelId = tabs.find((t) => t.id === activeTabId)?.modelId ?? null;

  const filteredModels = useMemo(() => {
    if (!modelsData) return [];
    let models = modelsData.models;
    if (selectedTask) models = models.filter((m) => m.task === selectedTask);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      models = models.filter(
        (m) =>
          m.name.toLowerCase().includes(q) ||
          m.id.toLowerCase().includes(q) ||
          m.description.toLowerCase().includes(q) ||
          m.author.toLowerCase().includes(q) ||
          m.tags.some((t) => t.toLowerCase().includes(q)),
      );
    }
    return models;
  }, [modelsData, selectedTask, searchQuery]);

  if (!sidebarOpen) {
    return (
      <div className="w-12 flex flex-col items-center py-4 border-r border-border bg-card gap-2">
        <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)} title="Open sidebar">
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Separator />
        <Button variant={activeView === 'models' ? 'secondary' : 'ghost'} size="icon"
          onClick={() => { setActiveView('models'); setSidebarOpen(true); }} title="Models">
          <Sparkles className="h-4 w-4" />
        </Button>
        <Button variant={activeView === 'favorites' ? 'secondary' : 'ghost'} size="icon"
          onClick={() => { setActiveView('favorites'); setSidebarOpen(true); }} title="Favorites">
          <Star className="h-4 w-4" />
        </Button>
        <Button variant={activeView === 'history' ? 'secondary' : 'ghost'} size="icon"
          onClick={() => { setActiveView('history'); setSidebarOpen(true); }} title="History">
          <History className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  return (
    <div className="w-64 flex flex-col border-r border-border bg-card h-full overflow-hidden">
      {/* Header */}
      <div className="p-3 flex items-center justify-between border-b border-border">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          <span className="font-semibold text-sm">AI Playground</span>
        </div>
        <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(false)} className="h-7 w-7">
          <ChevronLeft className="h-4 w-4" />
        </Button>
      </div>

      {/* View tabs */}
      <div className="flex gap-1 p-2 border-b border-border">
        {(['models', 'favorites', 'history'] as const).map((view) => (
          <button key={view} onClick={() => setActiveView(view)}
            className={cn(
              'flex-1 flex items-center justify-center gap-1 rounded-lg py-1 text-xs font-medium transition-colors',
              activeView === view ? 'bg-accent text-accent-foreground' : 'text-muted-foreground hover:text-foreground',
            )}>
            {view === 'models' && <Sparkles className="h-3 w-3" />}
            {view === 'favorites' && <Star className="h-3 w-3" />}
            {view === 'history' && <History className="h-3 w-3" />}
            <span className="capitalize">{view}</span>
          </button>
        ))}
      </div>

      {activeView === 'models' && (
        <>
          {/* Search */}
          <div className="p-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
              <Input placeholder="Search models..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-8 text-xs rounded-lg" />
            </div>
          </div>

          {/* Category tabs */}
          <div className="px-2 pb-2 flex gap-1 overflow-x-auto scrollbar-none">
            {CATEGORY_TABS.map((cat) => (
              <button key={String(cat.id)} onClick={() => setSelectedTask(cat.id as ModelTaskType | null)}
                className={cn(
                  'flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-medium whitespace-nowrap transition-all shrink-0',
                  selectedTask === cat.id
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'bg-secondary text-secondary-foreground hover:bg-accent',
                )}>
                <span>{cat.icon}</span>
                <span>{cat.label}</span>
              </button>
            ))}
          </div>

          {/* More task filters (if any) */}
          {tasksData && (
            <div className="px-2 pb-2 flex flex-wrap gap-1">
              {tasksData.tasks
                .filter((t) => t.count > 0 && !CATEGORY_TABS.some((c) => c.id === t.id))
                .map((t) => (
                  <button key={t.id} onClick={() => setSelectedTask(selectedTask === t.id ? null : t.id)}
                    className={cn(
                      'rounded-lg px-2 py-0.5 text-xs transition-colors',
                      selectedTask === t.id ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground hover:bg-accent',
                    )}>
                    {TASK_ICONS[t.id] ?? '\u2753'} {t.label}
                  </button>
                ))}
            </div>
          )}

          <Separator />

          {/* Model list */}
          <ScrollArea className="flex-1">
            <div className="p-2 space-y-1">
              {modelsLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
              ) : filteredModels.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-6">No models found</p>
              ) : (
                filteredModels.map((model) => (
                  <ModelListItem key={model.id} model={model} onSelect={() => openTab(model)}
                    isFavorite={favorites.some((f) => f.modelId === model.id)}
                    isActive={model.id === activeModelId} />
                ))
              )}
            </div>
          </ScrollArea>
        </>
      )}

      {activeView === 'favorites' && <FavoritesView />}
      {activeView === 'history' && <HistoryView />}
    </div>
  );
}

// â”€â”€â”€ Model list item â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function ModelListItem({ model, onSelect, isFavorite, isActive }: {
  model: ModelInfo;
  onSelect: () => void;
  isFavorite: boolean;
  isActive: boolean;
}) {
  return (
    <button onClick={onSelect}
      className={cn(
        'w-full text-left p-2.5 rounded-xl transition-all group border',
        isActive
          ? 'border-primary/60 bg-primary/8 shadow-sm'
          : 'border-transparent hover:bg-accent hover:border-border/60',
      )}
      style={isActive ? { background: 'rgba(249,115,22,0.08)' } : undefined}
    >
      <div className="flex items-start gap-2">
        <span className="text-base mt-0.5 shrink-0">{TASK_ICONS[model.task] ?? '\u2753'}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <span className={cn('text-xs font-medium truncate', isActive && 'text-primary')}>{model.name}</span>
            {model.featured && <span className="text-yellow-400 text-xs shrink-0">{'\u2605'}</span>}
            {isFavorite && <Star className="h-3 w-3 text-yellow-400 fill-yellow-400 shrink-0" />}
          </div>
          <p className="text-xs text-muted-foreground truncate mt-0.5">{model.author}</p>
          {model.capabilities.supportsStreaming && (
            <Badge variant="blue" className="mt-1 text-[10px] h-4 px-1">stream</Badge>
          )}
        </div>
        {isActive && <span className="shrink-0 w-1.5 h-1.5 rounded-full bg-primary mt-1.5" />}
      </div>
    </button>
  );
}

// â”€â”€â”€ Favorites view â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function FavoritesView() {
  const { favorites, removeFavorite, openTab } = usePlaygroundStore();
  const { data: modelsData } = useModels();

  if (favorites.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-4 text-center">
        <Star className="h-8 w-8 text-muted-foreground mb-2" />
        <p className="text-sm text-muted-foreground">No favorites yet</p>
        <p className="text-xs text-muted-foreground mt-1">Save model configurations you love</p>
      </div>
    );
  }

  return (
    <ScrollArea className="flex-1">
      <div className="p-2 space-y-1">
        {favorites.map((fav) => {
          const model = modelsData?.models.find((m) => m.id === fav.modelId);
          return (
            <div key={fav.id} className="p-2.5 rounded-xl bg-secondary group border border-transparent hover:border-border/60 transition-colors">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate">{fav.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{fav.modelName}</p>
                </div>
                <Button variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100 rounded-lg"
                  onClick={() => removeFavorite(fav.id)}>Ã—</Button>
              </div>
              {model && (
                <Button variant="outline" size="sm" className="mt-1.5 h-6 text-xs w-full rounded-lg" onClick={() => openTab(model)}>Load</Button>
              )}
            </div>
          );
        })}
      </div>
    </ScrollArea>
  );
}

// â”€â”€â”€ History view â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function HistoryView() {
  const { history, removeHistoryEntry, openTab } = usePlaygroundStore();
  const { data: modelsData } = useModels();

  if (history.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-4 text-center">
        <History className="h-8 w-8 text-muted-foreground mb-2" />
        <p className="text-sm text-muted-foreground">No history yet</p>
        <p className="text-xs text-muted-foreground mt-1">Your inference history will appear here</p>
      </div>
    );
  }

  return (
    <ScrollArea className="flex-1">
      <div className="p-2 space-y-1">
        {history.slice(0, 50).map((entry) => {
          const model = modelsData?.models.find((m) => m.id === entry.model);
          const task = entry.task as ModelTaskType;
          return (
            <div key={entry.id} className="p-2.5 rounded-xl bg-secondary group border border-transparent hover:border-border/60 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate">{TASK_ICONS[task] ?? '\u2753'} {model?.name ?? entry.model}</p>
                  <p className="text-xs text-muted-foreground">{new Date(entry.timestamp).toLocaleString()}</p>
                </div>
                <Button variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100 shrink-0 rounded-lg"
                  onClick={() => removeHistoryEntry(entry.id)}>Ã—</Button>
              </div>
              {model && (
                <Button variant="outline" size="sm" className="mt-1.5 h-6 text-xs w-full rounded-lg" onClick={() => openTab(model)}>Load</Button>
              )}
            </div>
          );
        })}
      </div>
    </ScrollArea>
  );
}

